import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Save, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { handleGSTInput, validateGST } from '@/utils/inputValidation';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ACCOUNT_TYPES = ['Proprietorship', 'Partnership', 'LLP', 'Pvt Ltd', 'Public Ltd', 'Trust', 'Society', 'Government', 'Other'];

const Field = ({ label, required, optional, error, children, hint }) => (
  <div className="flex items-start gap-6 py-3 border-b border-slate-100 last:border-0">
    <label className="w-48 flex-shrink-0 text-sm font-medium text-slate-600 pt-2">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}{optional}
    </label>
    <div className="flex-1 min-w-0">
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  </div>
);

// Real-time PAN position-based restriction:
// Positions 0-4 (1st-5th): letters A-Z only
// Positions 5-8 (6th-9th): digits 0-9 only
// Position 9 (10th): letter A-Z only
const handlePanInput = (raw) => {
  const upper = raw.toUpperCase();
  let result = '';
  for (let i = 0; i < Math.min(upper.length, 10); i++) {
    const ch = upper[i];
    if (i < 5 && /[A-Z]/.test(ch)) result += ch;
    else if (i >= 5 && i < 9 && /[0-9]/.test(ch)) result += ch;
    else if (i === 9 && /[A-Z]/.test(ch)) result += ch;
  }
  return result;
};

export default function AccountEditPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromDashboard = searchParams.get('from') === 'dashboard';
  const { user } = useAuth();
  const [form, setForm] = useState({
    account_name: '', account_type: '', industry_group: '', sub_vertical: '',
    pan: '', tan: '', duns: '', website: '', gst_number: '', mfa_enabled: false,
  });
  const [industryGroups, setIndustryGroups] = useState({});
  const [subVerticals, setSubVerticals] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPan, setShowPan] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [accRes, igRes] = await Promise.all([
          axios.get(`${API}/partner/account`),
          axios.get(`${API}/partner/portal/industry-groups`),
        ]);
        const acc = accRes.data;
        setForm({
          account_name: acc.account_name || '',
          account_type: acc.account_type || '',
          industry_group: acc.industry_group || '',
          sub_vertical: acc.sub_vertical || '',
          pan: acc.pan || '',
          tan: acc.tan || '',
          duns: acc.duns || '',
          website: acc.website || '',
          gst_number: acc.gst_number || '',
          mfa_enabled: acc.mfa_enabled || false,
        });
        setIndustryGroups(igRes.data);
        if (acc.industry_group && igRes.data[acc.industry_group]) {
          setSubVerticals(igRes.data[acc.industry_group]);
        }
      } catch { toast.error('Failed to load account details'); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  const handleIndustryChange = (ig) => {
    setForm(f => ({ ...f, industry_group: ig, sub_vertical: '' }));
    setSubVerticals(industryGroups[ig] || []);
  };

  const validate = () => {
    const e = {};
    if (form.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan.toUpperCase())) {
      e.pan = 'PAN must be ABCDE1234F';
    }
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/partner/account`, {
        ...form,
        pan: form.pan ? form.pan.toUpperCase() : undefined,
      });
      toast.success('Account details saved');
      navigate(fromDashboard ? '/reseller/profile' : '/partner/kyc-portal?tab=account');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4">
        <button
          onClick={() => navigate(fromDashboard ? '/reseller/profile' : '/partner/kyc-portal?tab=account')}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors"
          data-testid="back-to-account"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="h-5 w-px bg-slate-200" />
        <h1 className="text-lg font-semibold text-slate-800">Edit Account Details</h1>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
            <p className="text-sm text-slate-600">Update your account and business information below.</p>
            <p className="text-xs text-slate-400 mt-0.5">CRM ID, Account Status, and Account Category are system-managed and cannot be edited.</p>
          </div>
          <div className="px-6 py-4 divide-y divide-slate-100">
            <Field label="Account Name">
              <Input value={form.account_name} onChange={e => setForm(f => ({ ...f, account_name: e.target.value }))}
                placeholder="Your business name" data-testid="edit-account-name" />
            </Field>

            <Field label="Account Type">
              <select value={form.account_type} onChange={e => setForm(f => ({ ...f, account_type: e.target.value }))}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                data-testid="edit-account-type">
                <option value="">Select type...</option>
                {ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>

            <Field label="Industry Group" optional error={errors.industry_group}>
              <select value={form.industry_group} onChange={e => handleIndustryChange(e.target.value)}
                className={`w-full h-10 rounded-md border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white ${errors.industry_group ? 'border-red-400' : 'border-slate-200'}`}
                data-testid="edit-industry-group">
                <option value="">Select industry group...</option>
                {Object.keys(industryGroups).map(ig => <option key={ig} value={ig}>{ig}</option>)}
              </select>
            </Field>

            <Field label="Sub-Vertical" optional error={errors.sub_vertical}>
              <select value={form.sub_vertical} onChange={e => setForm(f => ({ ...f, sub_vertical: e.target.value }))}
                disabled={!form.industry_group}
                className={`w-full h-10 rounded-md border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white disabled:bg-slate-50 disabled:text-slate-400 ${errors.sub_vertical ? 'border-red-400' : 'border-slate-200'}`}
                data-testid="edit-sub-vertical">
                <option value="">Select sub-vertical...</option>
                {subVerticals.map(sv => <option key={sv} value={sv}>{sv}</option>)}
              </select>
            </Field>

            <Field label="GST Number" optional>
              <Input
                value={form.gst_number}
                onChange={e => setForm(f => ({ ...f, gst_number: e.target.value.toUpperCase() }))}
                placeholder="22AAAAA0000A1Z5"
                maxLength={15}
                className="font-mono text-sm"
                data-testid="edit-gst"
              />
            </Field>

            <Field label="PAN Number" error={errors.pan}>
              <div className="relative">
                <Input
                  value={form.pan}
                  onChange={e => setForm(f => ({ ...f, pan: handlePanInput(e.target.value) }))}
                  placeholder="ABCDE1234F"
                  maxLength={10}
                  type="text"
                  className={`font-mono text-sm ${errors.pan ? 'border-red-400' : ''}`}
                  data-testid="edit-pan"
                />
              </div>
            </Field>

            <Field label="TAN Number">
              <Input value={form.tan} onChange={e => setForm(f => ({ ...f, tan: e.target.value.toUpperCase() }))}
                placeholder="TAN Number" className="font-mono text-sm" data-testid="edit-tan" />
            </Field>

            <Field label="DUNS Number">
              <Input value={form.duns} onChange={e => setForm(f => ({ ...f, duns: e.target.value }))}
                placeholder="D-U-N-S Number" className="font-mono text-sm" data-testid="edit-duns" />
            </Field>

            <Field label="Website">
              <Input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                placeholder="https://yourcompany.com" type="url" data-testid="edit-website" />
            </Field>

            <Field label="MFA" hint="Enable two-factor authentication for your account">
              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={() => setForm(f => ({ ...f, mfa_enabled: !f.mfa_enabled }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.mfa_enabled ? 'bg-blue-600' : 'bg-slate-200'}`}
                  data-testid="edit-mfa-toggle"
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform shadow ${form.mfa_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className="text-sm text-slate-600">{form.mfa_enabled ? 'Enabled' : 'Disabled'}</span>
              </div>
            </Field>
          </div>

          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
            <Button variant="outline" onClick={() => navigate(fromDashboard ? '/reseller/profile' : '/partner/kyc-portal?tab=account')} data-testid="cancel-account-edit">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="save-account-details"
            >
              {saving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
